// The demo's Marketeurs: the catalogue brands (migrations 0048, 0049) with
// the example pilot of the design Ruben chose (38 bottles in Bastos, Essos,
// Melen and Mendong), in the shapes ops_marketers and ops_brand_view return
// (migration 0047). The brand view applies the same rule as Postgres: counts
// only, a quartier under 5 homes of the brand folded. Deterministic.
import { assetsFor } from './brands';
import type { Bottle, BottleStats, BrandRow, BrandView, Marketers } from './types';

const K = 5;
const Q = ['Bastos', 'Essos', 'Melen', 'Mendong'];

// code, name, paint, picture slug, homes per quartier (Q order), days until
// empty for the bottles due this week, full bottles in station, refills per week (oldest first)
const CAT: [string, string, string, string, number[], number[], number, number[]][] = [
  ['TRADEX', 'Tradex', '#1E3CCB', 'tradex', [3, 2, 1, 1], [0, 3, 6], 4, [2, 1, 3, 2, 2, 3, 4, 3]],
  ['TOTAL', 'TotalEnergies', '#0D2160', 'total', [1, 3, 1, 1], [2, 5], 6, [1, 2, 1, 2, 2, 2, 1, 2]],
  ['BOCOM', 'Bocom', '#1E8E4E', 'bocom', [0, 1, 2, 1], [2, 4], 1, [1, 0, 1, 1, 1, 2, 1, 2]],
  ['OLA', 'OLA Energy', '#CE1F1F', 'ola', [1, 0, 1, 1], [4], 3, [0, 1, 0, 1, 1, 0, 1, 1]],
  ['PLEIN GAZ', 'Plein Gaz', '#D71920', 'plein-gaz', [0, 1, 1, 1], [1], 0, [1, 1, 0, 1, 1, 0, 1, 1]],
  ['AFRIGAZ', 'AfriGaz', '#C4E6E2', 'afrigaz', [1, 0, 1, 1], [], 2, [0, 1, 1, 0, 1, 0, 0, 1]],
  ['TANKOIL', 'Tankoil', '#F2F2F0', 'tankoil', [1, 1, 1, 0], [4], 2, [1, 0, 0, 1, 1, 1, 0, 1]],
  ['GREEN OIL', 'Green Oil', '#12A486', 'greenoil', [0, 1, 0, 1], [], 1, [0, 0, 1, 0, 1, 0, 0, 1]],
  ['NEPTUNE', 'Neptune Gaz', '#1FA9DE', 'neptune', [1, 0, 1, 0], [5], 2, [0, 1, 0, 0, 0, 1, 1, 0]],
  ['STARGAS', 'StarGas', '#E06272', 'stargas', [0, 1, 0, 1], [1], 0, [1, 0, 1, 0, 0, 0, 1, 0]],
  ['GLOCALGAZ', 'Glocalgaz', '#FCD306', 'glocalgaz', [1, 0, 0, 0], [], 1, [0, 1, 0, 0, 0, 1, 0, 0]],
  ['BLESSING', 'Blessing', '#874DB3', 'blessing', [0, 0, 1, 0], [0], 0, [1, 0, 0, 0, 0, 0, 1, 0]],
  ['ISOGAZ', 'Isogaz', '#262728', 'isogaz', [0, 1, 0, 0], [], 0, [0, 0, 1, 0, 0, 0, 0, 0]],
  ['SCTM', 'SCTM', '#FC7E00', 'sctm', [0, 0, 0, 0], [], 0, [0, 0, 0, 0, 0, 0, 0, 0]],
];
const PACE = 3.6; // % of a 12.5 kg bottle a day, about 0.45 kg

export function bottleStats(rows: Bottle[]): BottleStats {
  const d7 = Array(7).fill(0), weeks8 = Array(8).fill(0);
  let lv = 0, nl = 0, due7 = 0;
  rows.forEach((b) => {
    if (b.level != null) { lv += b.level; nl++; }
    if (b.days_left != null && b.days_left < 7) { due7++; d7[Math.floor(b.days_left)]++; }
    b.refills_ago.forEach((a) => { const w = 7 - Math.floor(a / 7); if (w >= 0 && w < 8) weeks8[w]++; });
  });
  return { n: rows.length, level: nl ? Math.round(lv / nl) : 0, due7, d7, weeks8 };
}

function bottles(): Bottle[] {
  return CAT.flatMap(([code, , , slug, perQ, due, , weeks], bi) => {
    const qs = perQ.flatMap((n, qi) => Array<string>(n).fill(Q[qi]));
    const rows: Bottle[] = qs.map((q, i) => {
      const d = due[i];
      const level = d != null ? Math.round(4 + d * PACE) : Math.min(97, 34 + ((bi * 17 + i * 23) % 58));
      return { id: `mkb-${slug}-${i}`, quartier: q, brand: code, level, days_left: d != null ? d + 0.5 : +(level / PACE).toFixed(1), size_kg: 12.5, refills_ago: [] };
    });
    let k = 0;
    weeks.forEach((c, w) => { for (let j = 0; j < c; j++) rows[k++ % rows.length].refills_ago.push((7 - w) * 7 + 1 + (j % 6)); });
    return rows;
  });
}

export function demoMarketers(): Marketers {
  const bs = bottles();
  const brands: BrandRow[] = CAT.map(([code, name, color, slug, , , stock]) => ({
    code, name, brand_color: color, country: 'CM', stock, stats: bottleStats(bs.filter((x) => x.brand === code)), ...assetsFor(slug),
  }));
  return { generated_at: new Date().toISOString(), k: K, bottles: bs, brands };
}

export function demoBrandView(code: string): BrandView {
  const b = CAT.find((x) => x[0] === code);
  if (!b) throw new Error('no_such_brand');
  const [, name, color, slug, , , stock] = b;
  const rows = bottles().filter((x) => x.brand === code);
  const byQ = new Map<string, Bottle[]>();
  rows.forEach((r) => byQ.set(r.quartier, [...(byQ.get(r.quartier) ?? []), r]));
  const shown = [...byQ].filter(([, a]) => a.length >= K).sort((x, y) => y[1].length - x[1].length);
  const rest = [...byQ].filter(([, a]) => a.length < K);
  return {
    code, name, brand_color: color, k: K, stock, stats: bottleStats(rows), ...assetsFor(slug),
    bands: {
      month: rows.filter((r) => (r.days_left ?? 99) >= 30).length,
      under_month: rows.filter((r) => r.days_left != null && r.days_left >= 7 && r.days_left < 30).length,
      week: rows.filter((r) => r.days_left != null && r.days_left < 7).length,
    },
    quartiers: shown.map(([q, a]) => ({ quartier: q, ...bottleStats(a) })),
    others: { quartiers: rest.length, ...bottleStats(rest.flatMap(([, a]) => a)) },
  };
}
